import { useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Calendar, Factory, Package, DollarSign, MessageSquare, ShoppingCart, Settings, Menu, X, LogOut, Bell, User, ChevronLeft, Building2, FileText, Receipt, CreditCard, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Dashboard from "./Dashboard";
import EmployeeManagement from "./EmployeeManagement";
import AttendanceTracking from "./AttendanceTracking";
import ProductManagement from "./ProductManagement";
import InquiryManagement from "./InquiryManagement";
import ProductionManagement from "./ProductionManagement";
import InventoryManagement from "./InventoryManagement";
import FinancialDashboard from "./FinancialDashboard";
import PayrollSection from "./PayrollSection";
import CustomerManagement from "./CustomerManagement";
import SalesOrderManagement from "./SalesOrderManagement";
import InvoiceManagement from "./InvoiceManagement";
import PaymentManagement from "./PaymentManagement";
import UserManagement from "./UserManagement";

const menuItems = [
  { id: "dashboard", path: "/portal", label: "Dashboard", icon: LayoutDashboard, access: ["dashboard"] },
  { id: "customers", path: "/portal/customers", label: "Customers", icon: Building2, access: ["customers", "sales"] },
  { id: "sales-orders", path: "/portal/sales-orders", label: "Sales Orders", icon: FileText, access: ["customers", "sales"] },
  { id: "invoices", path: "/portal/invoices", label: "Invoices", icon: Receipt, access: ["customers", "sales"] },
  { id: "payments", path: "/portal/payments", label: "Payments", icon: CreditCard, access: ["customers", "sales"] },
  { id: "employees", path: "/portal/employees", label: "Employees", icon: Users, access: ["employees"] },
  { id: "attendance", path: "/portal/attendance", label: "Attendance", icon: Calendar, access: ["attendance"] },
  { id: "payroll", path: "/portal/payroll", label: "Payroll", icon: DollarSign, access: ["payroll"] },
  { id: "products", path: "/portal/products", label: "Products", icon: Package, access: ["products"] },
  { id: "inquiries", path: "/portal/inquiries", label: "CRM / Inquiries", icon: MessageSquare, access: ["crm", "sales"] },
  { id: "production", path: "/portal/production", label: "Production", icon: Factory, access: ["production"] },
  { id: "inventory", path: "/portal/inventory", label: "Inventory", icon: ShoppingCart, access: ["inventory"] },
  { id: "financial", path: "/portal/financial", label: "Financials", icon: DollarSign, access: ["financials"] },
  { id: "user-management", path: "/portal/user-management", label: "User Management", icon: Shield, access: ["user_management"] },
];

export default function PortalLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, employee, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const userAccess = user?.access || [];
  const filtered = menuItems.filter(item => item.access.some(a => userAccess.includes(a)));

  const handleLogout = () => { logout(); navigate("/"); toast.success("Logged out"); };

  const NavItem = ({ item, mobile }) => {
    const isActive = location.pathname === item.path || (item.path !== "/portal" && location.pathname.startsWith(item.path));
    return (
      <button onClick={() => { navigate(item.path); if (mobile) setMobileOpen(false); }}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all mb-1 ${isActive ? "bg-forest text-white" : "text-slate-400 hover:bg-white/10 hover:text-white"} ${!sidebarOpen && !mobile ? "justify-center" : ""}`}
        title={!sidebarOpen ? item.label : undefined} data-testid={`nav-${item.id}`}>
        <item.icon className="w-5 h-5 flex-shrink-0" />
        {(sidebarOpen || mobile) && <span className="text-sm font-sans">{item.label}</span>}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="portal-layout">
      {/* Desktop Sidebar */}
      <aside className={`fixed left-0 top-0 bottom-0 bg-[#0F172A] text-white z-40 transition-all duration-300 hidden lg:block ${sidebarOpen ? "w-64" : "w-16"}`} data-testid="portal-sidebar">
        <div className={`h-16 flex items-center ${sidebarOpen ? "px-5" : "justify-center"} border-b border-white/10`}>
          {sidebarOpen ? (
            <div className="flex items-center gap-2">
              <img src="/images/logo.png" alt="Logo" className="w-8 h-8 object-contain rounded" />
              <span className="font-serif font-bold text-sm">Portal</span>
            </div>
          ) : <img src="/images/logo.png" alt="Logo" className="w-8 h-8 object-contain rounded" />}
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="absolute -right-3 top-20 w-6 h-6 bg-forest rounded-full flex items-center justify-center text-white shadow-md z-50">
          {sidebarOpen ? <ChevronLeft className="w-3 h-3" /> : <Menu className="w-3 h-3" />}
        </button>
        <nav className="mt-4 px-2 overflow-y-auto" style={{ maxHeight: "calc(100vh - 140px)" }}>
          {filtered.map(item => <NavItem key={item.id} item={item} />)}
        </nav>
        <div className={`absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 ${!sidebarOpen ? "flex justify-center" : ""}`}>
          <button onClick={handleLogout} className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors" data-testid="logout-btn">
            <LogOut className="w-5 h-5" />{sidebarOpen && <span className="text-sm font-sans">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-[#0F172A] text-white">
            <div className="h-16 flex items-center px-5 border-b border-white/10 justify-between">
              <div className="flex items-center gap-2">
                <img src="/images/logo.png" alt="Logo" className="w-8 h-8 object-contain rounded" />
                <span className="font-serif font-bold text-sm">Portal</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="text-white"><X className="w-5 h-5" /></button>
            </div>
            <nav className="p-3">{filtered.map(item => <NavItem key={item.id} item={item} mobile />)}</nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className={`transition-all duration-300 min-h-screen ${sidebarOpen ? "lg:ml-64" : "lg:ml-16"}`}>
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-slate-100" data-testid="mobile-sidebar-btn">
              <Menu className="w-6 h-6" />
            </button>
            <div className="hidden sm:block" />
            <div className="flex items-center gap-4">
              <button className="relative p-2 rounded-lg hover:bg-slate-100" data-testid="notifications-btn">
                <Bell className="w-5 h-5 text-slate-500" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </button>
              <div className="flex items-center gap-3">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-navy font-sans">{employee?.name || user?.username}</p>
                  <p className="text-xs text-slate-400 capitalize font-sans">{(user?.role || "").replace("_", " & ")}</p>
                </div>
                <div className="w-10 h-10 bg-navy rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/customers" element={<CustomerManagement />} />
            <Route path="/sales-orders" element={<SalesOrderManagement />} />
            <Route path="/invoices" element={<InvoiceManagement />} />
            <Route path="/payments" element={<PaymentManagement />} />
            <Route path="/employees" element={<EmployeeManagement />} />
            <Route path="/attendance" element={<AttendanceTracking />} />
            <Route path="/payroll" element={<PayrollSection />} />
            <Route path="/products" element={<ProductManagement />} />
            <Route path="/inquiries" element={<InquiryManagement />} />
            <Route path="/production" element={<ProductionManagement />} />
            <Route path="/inventory" element={<InventoryManagement />} />
            <Route path="/financial" element={<FinancialDashboard />} />
            <Route path="/user-management" element={<UserManagement />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
